import { useCallback, useEffect, useRef, useState } from 'react';
import { formatPence } from '@quiz/shared';
import { api, ApiError } from '../lib/api.js';
import { Screen, Button, Card, Banner, Spinner } from '../components/ui.js';

interface CurrentEvent {
  id: string;
  name: string;
  entryFeePence: number;
  isFree: boolean;
}

interface CheckoutStatus {
  status: 'PENDING' | 'PAID' | 'REDEEMED' | 'REFUNDED' | 'FAILED' | 'EXPIRED';
  code?: string;
  canRetry: boolean;
}

/**
 * Card entry. Deliberately not optimistic: a payment shows as confirmed only
 * when the server says so, and the server only says so after re-fetching the
 * checkout from the provider.
 */
export function PayScreen() {
  const [event, setEvent] = useState<CurrentEvent | null>(null);
  const [ticketId, setTicketId] = useState<string | null>(
    () => new URLSearchParams(location.search).get('ticket'),
  );
  const [status, setStatus] = useState<CheckoutStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    api<CurrentEvent>('/api/event/current')
      .then(setEvent)
      .catch(() => setError('There is no quiz set up right now.'));
  }, []);

  const poll = useCallback(async (id: string) => {
    try {
      const next = await api<CheckoutStatus>(`/api/checkout/${id}`);
      setStatus(next);
      return next;
    } catch {
      return null;
    }
  }, []);

  // A 15-second poll runs regardless of the webhook, so a dropped webhook never
  // strands somebody standing at the door with their phone out.
  useEffect(() => {
    if (!ticketId) return;
    void poll(ticketId);
    timer.current = window.setInterval(() => {
      void poll(ticketId).then((s) => {
        if (s && (s.status === 'PAID' || s.status === 'REDEEMED') && timer.current) {
          clearInterval(timer.current);
        }
      });
    }, 15_000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [ticketId, poll]);

  async function startCheckout() {
    if (!event) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ ticketId: string; hostedCheckoutUrl: string }>('/api/checkout', {
        method: 'POST',
        body: { eventId: event.id },
      });
      setTicketId(res.ticketId);
      // Hosted checkout: no card details ever touch our server.
      location.href = res.hostedCheckoutUrl;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start the payment.');
      setBusy(false);
    }
  }

  if (!event) {
    return (
      <Screen>
        {error ? <Banner kind="error">{error}</Banner> : <Spinner label="Loading" />}
      </Screen>
    );
  }

  if (event.isFree) {
    return (
      <Screen>
        <Card className="text-center">
          <p className="text-xl font-semibold">Tonight is free</p>
          <p className="mt-2 text-muted">Just use the join code on your table.</p>
          <div className="mt-4">
            <Button
              onClick={() => {
                location.href = '/';
              }}
            >
              Go to the join screen
            </Button>
          </div>
        </Card>
      </Screen>
    );
  }

  const paid = status?.status === 'PAID' || status?.status === 'REDEEMED';

  return (
    <Screen>
      <header className="mb-6 mt-4 text-center">
        <h1 className="text-2xl font-bold">{event.name}</h1>
        <p className="mt-1 text-muted">Entry {formatPence(event.entryFeePence)}</p>
      </header>

      {paid && status?.code ? (
        <Card className="text-center">
          <p className="text-xs uppercase tracking-wide text-muted">Paid. Your code is</p>
          <p className="my-4 font-mono text-5xl tracking-[0.2em] text-go">{status.code}</p>
          {/* The whole point of this screen. It was a text link, which is a
              thin thing to hand somebody who has just paid. */}
          <Button
            variant="go"
            onClick={() => {
              location.href = `/?code=${status.code}`;
            }}
          >
            Join the quiz
          </Button>
        </Card>
      ) : ticketId ? (
        <div className="space-y-4">
          <Card className="text-center">
            <p className="text-lg font-semibold">
              {status?.status === 'PENDING' ? 'Waiting for the payment to confirm…' : null}
              {status?.status === 'FAILED' ? 'That payment did not go through.' : null}
              {status?.status === 'EXPIRED' ? 'That payment link timed out.' : null}
              {!status ? 'Checking…' : null}
            </p>
            <p className="mt-2 text-sm text-muted">
              We confirm with the payment provider directly, so this can take a few seconds.
            </p>
          </Card>
          {status?.canRetry && (
            <Button onClick={() => void startCheckout()} disabled={busy}>
              Try again
            </Button>
          )}
          <p className="text-center text-sm text-muted">
            Stuck? Pay cash at the door and they will hand you a code.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {error && <Banner kind="error">{error}</Banner>}
          <Button onClick={() => void startCheckout()} disabled={busy}>
            {busy ? 'Opening…' : `Pay ${formatPence(event.entryFeePence)} by card`}
          </Button>
          <p className="text-center text-xs text-muted">
            Card details are handled by the payment provider. They never reach us.
          </p>
        </div>
      )}
    </Screen>
  );
}
